'use client'

// The bubble over his head. Sam, 3 Nov: "when you get close to Pino, a little thought bubble can show above his head,
// with a picture of what he is carrying in his beak (just choose one thing), it's an indicator for people to show
// something they can open. We may use this little bubble to put other things in too to expand on this."
//
// A cloud with two small circles trailing down to his head, and in the cloud the picture his bag's pop-up has of the
// newest thing in his bag (components/ui/bagPictures.js; his beak merged into his bag that afternoon, Sam: "yes merge
// them"). It shows while the camera is within about twelve metres of him and he has something in his bag. A tap on
// it, or the crosshair on it while walking, opens his bag the way a tap on him does (BirdHit.jsx). It stays out of the
// views where his bag does not open (the drone, photo mode, the Pino cam, the vending machine's glass, the job centre's
// window), and away while the bag is open, which is what it points to.
//
// What it costs. One sprite and one material, made once and kept. It is mounted from the start, hidden, so the boot
// pass builds its pipeline behind the veil (effects/Precompiler.jsx) and its first appearance is not a compile. Its
// picture is a small canvas texture, drawn on the page once each time what he carries changes, and only once somebody
// is near: until then nothing is loaded and no renderer for the pictures is made. Each frame it reads two numbers off
// the camera and his pose and, while it shows, sets a position and a scale. No React state, no allocation. One draw
// while it shows, none while it does not. It is mounted after the bridge, so it reads where he is drawn this frame and
// not the frame before.
//
// What it shows comes from a source (BEAK below, named for the beak the thing it shows was in until 3 Nov): what to
// show now, a picture of it, and what a tap opens. His bag is the only one for now; anything else that wants to show
// over his head is another source. (The walker's crosshair counts the bubble as him and opens his bag, BirdHit
// aimsAtBird: a source that opens something else has to change that too.)

import { useEffect, useMemo, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three/webgpu'
import useGameStore from '../../../stores/gameStore'
import { birdPose } from '../../../lib/world/birdPose'
import { kawaii } from '../../../lib/ui/theme'
import { ditherMaterial } from '../effects/ditherRegistry'
import { holdPictures, pictureOf } from '../../ui/bagPictures'
import { birdHit, elsewhere, openBag, pressDown, pressUp, useHand } from './BirdHit'

// How near the camera has to be, in metres, to his middle.
const NEAR = 12
const MIDDLE = 1.1

// The canvas the bubble is drawn on, in pixels, shown in big ones (nearest filtered, as the world's textures are). The
// cloud is a run of overlapping circles with an outline round the lot, and two smaller ones trail down to the bottom
// left corner, which is the point that sits over his head. The picture goes in the middle of the cloud.
const W = 128
const H = 140
const LINE = 4
const PUFFS = [[40, 56, 20], [53, 35, 20], [77, 28, 21], [101, 39, 20], [107, 61, 16], [91, 80, 19], [64, 83, 19], [43, 75, 16], [73, 57, 35]]
const TRAIL = [[40, 113, 8], [25, 129, 5]]
const SHAPES = PUFFS.concat(TRAIL)
// The room for the picture in the middle of the cloud, and how far it may be blown up to fill it.
const INNER = { x: 37, y: 31, w: 72, h: 52 }
const MOST = 3
// with nothing to show in it (no picture for the thing, or none can be drawn here): three dots, as a thought
const DOTS = [[57, 57], [73, 57], [89, 57]]
const ANCHOR_X = 25 / W

// Where it sits: its bottom corner just over the top of his head (2.18 at the scale he is drawn, his head forward of
// his middle, Halloween.jsx's hat), so it turns with him; a metre across; a gentle bob; and a short pop on showing.
const LIFT = 2.32
const HEAD_AHEAD = 0.45
const SIZE = 1.0
const BOB = 0.04
const BOB_RATE = 2.2
const POP_S = 0.18
const POP_FROM = 0.55

/** The newest thing in his bag, as its picture from the pop-up's renderer (held for the drawing, let go after). */
async function beakPicture(look) {
  const letGo = holdPictures()
  try {
    const url = await pictureOf(look)
    if (!url) return null
    const img = new Image()
    img.src = url
    await img.decode()
    return img
  } finally {
    letGo()
  }
}

/**
 * What the bubble shows: the newest thing in his bag (sim-server world/Hoard.js carryingForViewer).
 *   now()       what to show, read every frame: the same object for the same thing, a new one only when it changes,
 *               null for nothing (the bridge writes birdPose.carrying that way)
 *   picture(x)  a promise of an image to draw in the cloud, or of null for the cloud with nothing in it
 *   open()      what a tap on it opens
 */
export const BEAK = { now: () => birdPose.carrying, picture: beakPicture, open: openBag }

// The part of a picture with something in it. A picture is drawn on a clear stage with the thing in the middle and
// room round it for any turn, so a flat thing (a doughnut, seen from a little above) is a sliver across it: shown
// whole, the first doughnut was a few pixels in the middle of the cloud and could not be told for anything.
let scratch = null
function filled(picture) {
  const w = picture.naturalWidth || picture.width
  const h = picture.naturalHeight || picture.height
  scratch ||= document.createElement('canvas')
  scratch.width = w
  scratch.height = h
  const c = scratch.getContext('2d', { willReadFrequently: true })
  c.clearRect(0, 0, w, h)
  c.drawImage(picture, 0, 0)
  const px = c.getImageData(0, 0, w, h).data
  let x0 = w
  let y0 = h
  let x1 = -1
  let y1 = -1
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (px[(y * w + x) * 4 + 3] < 128) continue
      x0 = Math.min(x0, x)
      x1 = Math.max(x1, x)
      y0 = Math.min(y0, y)
      y1 = Math.max(y1, y)
    }
  }
  return x1 < 0 ? { x: 0, y: 0, w, h } : { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 }
}

function draw(ctx, picture) {
  ctx.clearRect(0, 0, W, H)
  // the outline is the same circles drawn bigger in ink underneath: one path, so where they overlap there is no line
  for (const [grow, colour] of [[LINE, kawaii.ink], [0, kawaii.cream]]) {
    ctx.fillStyle = colour
    ctx.beginPath()
    for (const [x, y, r] of SHAPES) {
      ctx.moveTo(x + r + grow, y)
      ctx.arc(x, y, r + grow, 0, Math.PI * 2)
    }
    ctx.fill()
  }
  if (picture) {
    // blown up to fill the room in the cloud, by whole pixels where it can be so every pixel of it is the same size
    const box = filled(picture)
    let k = Math.min(INNER.w / box.w, INNER.h / box.h, MOST)
    if (k >= 1) k = Math.floor(k)
    const w = Math.round(box.w * k)
    const h = Math.round(box.h * k)
    ctx.imageSmoothingEnabled = false
    ctx.drawImage(picture, box.x, box.y, box.w, box.h, Math.round(INNER.x + (INNER.w - w) / 2), Math.round(INNER.y + (INNER.h - h) / 2), w, h)
  } else {
    ctx.fillStyle = kawaii.ink
    ctx.beginPath()
    for (const [x, y] of DOTS) {
      ctx.moveTo(x + 5, y)
      ctx.arc(x, y, 5, 0, Math.PI * 2)
    }
    ctx.fill()
  }
}

function makeBubble() {
  const canvas = document.createElement('canvas')
  canvas.width = W
  canvas.height = H
  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  texture.magFilter = THREE.NearestFilter
  texture.minFilter = THREE.NearestFilter
  texture.generateMipmaps = false
  // cut out at the edge rather than blended: crisp in the PSX way, drawn with the opaque things, nothing to sort
  const material = new THREE.SpriteNodeMaterial({ map: texture, transparent: false, alphaTest: 0.5 })
  // dithered at birth, like everything made after the world is up (effects/ditherRegistry.js)
  ditherMaterial(material)
  const sprite = new THREE.Sprite(material)
  sprite.name = 'thought_bubble'
  sprite.center.set(ANCHOR_X, 0)
  sprite.visible = false
  // hidden until somebody is near: the boot pass shows it once to build it, and the flag says it was meant to be
  sprite.userData.precompile = true
  // a hidden sprite still answers a ray (three does not ask whether it shows), and a tap must not find it then
  sprite.raycast = function raycast(raycaster, intersects) {
    if (this.visible) THREE.Sprite.prototype.raycast.call(this, raycaster, intersects)
  }
  return { canvas, ctx: canvas.getContext('2d'), texture, sprite }
}

// Draws what is to be shown, once, when somebody first comes near after it changed.
function paint(bubble, source, thing, st) {
  Promise.resolve()
    .then(() => source.picture(thing))
    .catch(() => null)
    .then((picture) => {
      // he changed what he carries while it was being drawn: the next one is asked for
      if (st.thing !== thing || !bubble.ctx) return
      draw(bubble.ctx, picture)
      bubble.texture.needsUpdate = true
      st.ready = true
    })
}

export function ThoughtBubble({ source = BEAK }) {
  const bubble = useMemo(() => makeBubble(), [])
  const hand = useHand()
  // what is drawn on it now, and since when it has shown
  const st = useRef({ thing: null, asked: false, ready: false, shownAt: 0 }).current

  // the views it stays out of, kept by a subscription so the frame reads one flag
  const off = useRef(true)
  useEffect(() => {
    const check = (s) => { off.current = elsewhere(s) || s.bagOpen }
    check(useGameStore.getState())
    return useGameStore.subscribe(check)
  }, [])

  // for the walker's crosshair (BirdHit aimsAtBird)
  useEffect(() => {
    birdHit.bubble = bubble.sprite
    return () => { if (birdHit.bubble === bubble.sprite) birdHit.bubble = null }
  }, [bubble])

  useFrame(({ camera, clock }) => {
    const sprite = bubble.sprite
    const thing = source.now()
    if (thing !== st.thing) {
      st.thing = thing
      st.asked = false
      st.ready = false
    }
    let show = false
    if (thing && birdPose.seen && !off.current) {
      const dx = camera.position.x - birdPose.x
      const dy = camera.position.y - (birdPose.y + MIDDLE)
      const dz = camera.position.z - birdPose.z
      if (dx * dx + dy * dy + dz * dz < NEAR * NEAR) {
        if (!st.asked) {
          st.asked = true
          paint(bubble, source, thing, st)
        }
        show = st.ready
      }
    }
    if (show !== sprite.visible) {
      sprite.visible = show
      if (show) st.shownAt = clock.elapsedTime
    }
    if (!show) return
    const t = clock.elapsedTime
    const grown = Math.min(1, (t - st.shownAt) / POP_S)
    const k = SIZE * (POP_FROM + (1 - POP_FROM) * grown * (2 - grown))
    sprite.scale.set(k, (k * H) / W, 1)
    sprite.position.set(
      birdPose.x + Math.sin(birdPose.rotY) * HEAD_AHEAD,
      birdPose.y + LIFT + Math.sin(t * BOB_RATE) * BOB,
      birdPose.z + Math.cos(birdPose.rotY) * HEAD_AHEAD,
    )
  })

  return (
    <primitive
      object={bubble.sprite}
      onPointerDown={pressDown}
      onPointerUp={(e) => pressUp(e, source.open)}
      onPointerOver={hand.over}
      onPointerOut={hand.out}
    />
  )
}

export default ThoughtBubble
